// The message contract between the native shell and the web app it hosts
// (ADR 008: the WebView is the whole app; native is splash, scan, checkout
// sheet and haptics).
//
// Deliberately hand-validated rather than zod-schema'd: this package must stay a
// dependency-free leaf that both a Metro (mobile) and a Turbopack (web) bundle
// can pull in without dragging a schema library across the bridge boundary.
// Each direction's parser table is a mapped type over its union's `type`, so a
// variant added without a parser is a compile error, not a silent `null`.

import type { EditorDoc } from '@sweetspot/core';

/** Bump when a message changes shape incompatibly. Parsers reject other majors. */
export const NATIVE_BRIDGE_VERSION = 2 as const;

// ── web → native ─────────────────────────────────────────────────────────

/** iOS feedback generators, one-to-one: `selection` is UISelectionFeedbackGenerator,
 *  `light`/`medium` are UIImpactFeedbackGenerator styles, the last three are
 *  UINotificationFeedbackGenerator outcomes. */
export type HapticKind = 'selection' | 'light' | 'medium' | 'success' | 'warning' | 'error';

export const HAPTIC_KINDS: readonly HapticKind[] = ['selection', 'light', 'medium', 'success', 'warning', 'error'];

export type WebToNativeMessage =
  /** The page has mounted and its bridge listener is armed. Native drops its
   *  splash / loading state. */
  | { type: 'ready'; path: string }
  /** A link that must leave the app (docs, a retailer). Opened in an in-app
   *  Safari sheet, never inside the WebView. */
  | { type: 'open-external'; url: string }
  | { type: 'haptic'; kind: HapticKind }
  /** Run the LiDAR room scan. Only sent when `capabilities.scan`. Answered with
   *  exactly one of `scan-result`, `scan-cancelled`, `scan-failed`. */
  | { type: 'start-scan' }
  /** Open a checkout URL in an in-app Safari sheet — Apple Pay works there and
   *  not inside a WKWebView that injects scripts. Only sent when
   *  `capabilities.checkout === 'web'`. Native allowlists the host. Answered
   *  with `checkout-closed` when the sheet goes away for any reason. */
  | { type: 'open-checkout'; url: string };

/** The web's post-payment page, loaded inside the Safari sheet, navigates here;
 *  the shell dismisses the sheet on it, which then answers `checkout-closed`. */
export const CHECKOUT_RETURN_URL = 'sweetspot://checkout-complete' as const;

// ── native → web ─────────────────────────────────────────────────────────

export type NativeToWebMessage =
  /** A finished scan, already mapped by `@sweetspot/room-scan`. The web
   *  merges its geometry into the room being drawn (`applyScannedGeometry`). */
  | { type: 'scan-result'; doc: EditorDoc }
  | { type: 'scan-cancelled' }
  | { type: 'scan-failed'; message: string }
  /** The checkout sheet closed: paid, abandoned, or dismissed. The web asks the
   *  backend which (the pending-checkout claim), so nothing is inferred here. */
  | { type: 'checkout-closed' };

export type BridgeMessage = WebToNativeMessage | NativeToWebMessage;

type Envelope<T> = T & { v: typeof NATIVE_BRIDGE_VERSION };

// ── encoding ─────────────────────────────────────────────────────────────

export function encodeMessage(message: BridgeMessage): string {
  const envelope: Envelope<BridgeMessage> = { v: NATIVE_BRIDGE_VERSION, ...message };
  return JSON.stringify(envelope);
}

/** DOM event name the web side listens on for native → web messages. */
export const NATIVE_MESSAGE_EVENT = 'rt-native-message' as const;

/**
 * JavaScript for `WebView.injectJavaScript`: dispatches `message` to the page
 * as a `CustomEvent`. The JSON is embedded via `JSON.stringify` twice — once to
 * serialise the envelope, once to make that string a valid JS string literal —
 * so a quote or a `</script>` inside a scanned room's data can't break out of
 * the literal. Ends in `true;` because react-native-webview logs a warning
 * when an injected script's completion value isn't a primitive.
 */
export function buildInjectScript(message: NativeToWebMessage): string {
  const literal = JSON.stringify(encodeMessage(message));
  return (
    `(function(){try{window.dispatchEvent(new CustomEvent(${JSON.stringify(NATIVE_MESSAGE_EVENT)},` +
    `{detail:JSON.parse(${literal})}));}catch(e){}})();true;`
  );
}

// ── decoding ─────────────────────────────────────────────────────────────

type Fields = Record<string, unknown>;

type ParserTable<U extends { type: string }> = {
  [K in U['type']]: (m: Fields) => Extract<U, { type: K }> | null;
};

function isRecord(x: unknown): x is Fields {
  return typeof x === 'object' && x !== null && !Array.isArray(x);
}

function oneOf<T extends string>(list: readonly T[], x: unknown): x is T {
  return typeof x === 'string' && (list as readonly string[]).includes(x);
}

function isHttpUrl(x: unknown): x is string {
  return typeof x === 'string' && /^https?:\/\//.test(x);
}

const MAX_FAILURE_MESSAGE = 500;

const WEB_TO_NATIVE_PARSERS: ParserTable<WebToNativeMessage> = {
  ready: (m) => (typeof m.path === 'string' ? { type: 'ready', path: m.path } : null),
  'open-external': (m) => (isHttpUrl(m.url) ? { type: 'open-external', url: m.url } : null),
  haptic: (m) => (oneOf(HAPTIC_KINDS, m.kind) ? { type: 'haptic', kind: m.kind } : null),
  'start-scan': () => ({ type: 'start-scan' }),
  'open-checkout': (m) => (isHttpUrl(m.url) ? { type: 'open-checkout', url: m.url } : null),
};

const NATIVE_TO_WEB_PARSERS: ParserTable<NativeToWebMessage> = {
  // Structural check only — the editor's own `normalizeEditorDoc` is the
  // authority on document shape. Here we just refuse obvious garbage.
  'scan-result': (m) =>
    isRecord(m.doc) && isRecord(m.doc.dims)
      ? { type: 'scan-result', doc: m.doc as unknown as EditorDoc }
      : null,
  'scan-cancelled': () => ({ type: 'scan-cancelled' }),
  'scan-failed': (m) =>
    typeof m.message === 'string'
      ? { type: 'scan-failed', message: m.message.slice(0, MAX_FAILURE_MESSAGE) }
      : null,
  'checkout-closed': () => ({ type: 'checkout-closed' }),
};

function parseWith<U extends { type: string }>(parsers: ParserTable<U>, raw: unknown): U | null {
  let value: unknown = raw;
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value);
    } catch {
      return null;
    }
  }
  if (!isRecord(value)) return null;
  if (value.v !== NATIVE_BRIDGE_VERSION) return null;
  const type = value.type;
  // Own keys only, so `type: "toString"` or `"__proto__"` can't reach a
  // prototype method.
  if (typeof type !== 'string' || !Object.prototype.hasOwnProperty.call(parsers, type)) return null;
  const parse = (parsers as unknown as Record<string, (m: Fields) => U | null>)[type];
  return parse(value);
}

/** Validates a raw `onMessage` payload from the WebView. `null` = ignore it. */
export function parseWebToNative(raw: unknown): WebToNativeMessage | null {
  return parseWith(WEB_TO_NATIVE_PARSERS, raw);
}

/** Validates the `detail` of a `NATIVE_MESSAGE_EVENT` on the web side. */
export function parseNativeToWeb(raw: unknown): NativeToWebMessage | null {
  return parseWith(NATIVE_TO_WEB_PARSERS, raw);
}
